import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Skeleton } from 'primereact/skeleton';
import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useSelector } from 'react-redux';
import { Pagination } from '../../utils/Pagination';
import styles from './SimpleTable.module.scss';

export const ButtonsExportar = ({ onExportar = (tipo) => tipo }) => {
    return (
        <>
            <Button
                tooltip="Exportar em PDF"
                tooltipOptions={{ position: 'top' }}
                label=""
                icon="pi pi-file-pdf"
                className="p-button-raised p-button-secondary"
                onClick={async () => onExportar('pdf')}
                style={{ marginRight: '10px' }}
            />
            <Button
                tooltip="Exportar em Excel"
                tooltipOptions={{ position: 'top' }}
                label=""
                icon="pi pi-file-excel"
                className="p-button-raised p-button-secondary"
                onClick={async () => onExportar('excel')}
                style={{ marginRight: '10px' }}
            />
            <Button
                tooltip="Exportar em CSV"
                tooltipOptions={{ position: 'top' }}
                label=""
                icon="pi pi-file"
                className="p-button-raised p-button-secondary"
                onClick={async () => onExportar('csv')}
                style={{ marginRight: '10px' }}
            />
            <Button
                tooltip="Imprimir"
                tooltipOptions={{ position: 'top' }}
                label=""
                icon="pi pi-print"
                className="p-button-raised p-button-secondary"
                onClick={async () => onExportar('print')}
                style={{ marginRight: '10px' }}
            />
        </>
    );
};

const TableLoader = (props) => {
    const products = Array.from({ length: 10 });

    const bodyTemplate = () => {
        return <Skeleton></Skeleton>;
    };

    const renderHeader = () => {
        if (!(props.onAdicionarDado || props.onModalSearch || props.onSearch)) return;

        return (
            <div className="table-header p-grid p-fluid p-justify-between p-align-center">
                {props.onModalSearch && (
                    <div className="p-col-12 p-lg-4" style={{ display: 'flex' }}>
                        {props.onExportar && (
                            <div>
                                <Skeleton width="4rem" height="2rem"></Skeleton>
                                <Skeleton width="4rem" height="2rem"></Skeleton>
                                <Skeleton width="4rem" height="2rem"></Skeleton>
                                <Skeleton width="4rem" height="2rem"></Skeleton>
                            </div>
                        )}
                    </div>
                )}
                {props.onSearch && (
                    <div className=" p-col-12 p-md-4">
                        <div className="p-grid p-fluid p-align-center">
                            <div className="p-col">
                                <Skeleton height="2rem" className="mb-2"></Skeleton>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="datatable-responsive-demo p-col-12">
            <DataTable
                className={'p-datatable-responsive-demo'}
                value={products}
                header={renderHeader()}
                responsiveLayout="stack"
                breakpoint="960px"
            >
                {props.camposTabela &&
                    props.camposTabela.map((campo, i) => (
                        <Column
                            field={campo.nome}
                            header={campo.titulo}
                            body={bodyTemplate}
                            key={i}
                        />
                    ))}
                {props.children}
                {props.hasEventoAcao && (
                    <Column
                        headerStyle={{
                            width: props.tamnhoAcao ? props.tamnhoAcao : '8em',
                            textAlign: 'center',
                        }}
                        bodyStyle={{ textAlign: 'center' }}
                        body={<Skeleton shape="circle" size="3rem" className="mr-2"></Skeleton>}
                        header="Ação"
                    />
                )}
            </DataTable>
        </div>
    );
};

const SimpleTable = (
    {
        onFind,
        children,
        paginator = true,
        className = '',
        data = [],
        col = '12',
        onSetList = (res) => res,
        ...props
    },
    ref
) => {
    const {
        inicioSeletor,
        page,
        rows,
        onSelectionChange,
        selectionMode,
        onRowSelect,
        rowExpansionTemplate,
        onRowExpand,
        onRowCollapse,
        ...rest
    } = props;

    const count = useSelector((state) => state.counterRequestReducer.counterRequest);
    const isLoading = count > 0;
    const [selected, setSelected] = useState(inicioSeletor ? inicioSeletor : null);
    const [stateRows, setStateRows] = useState(10);
    const [expandedRows, setExpandedRows] = useState(null);
    const [lista, setLista] = useState(data);
    const [initialTableData, setInitialTableData] = useState(data);
    const [totalRecords, setTotalRecords] = useState(0);
    const [pagination, setPagination] = useState(new Pagination());
    const [lazyParams, setLazyParams] = useState({
        first: 0,
        rows: 10,
        page: 0,
    });
    const [permitidoExecutarFuncao, setPermitidoExecutarFuncao] = useState(
        page !== undefined && page !== null ? false : true
    );
    const onPaginacao = (event) => {
        pagination.paginate(event);
        setPagination(pagination);
        onPesquisar(pagination);
    };

    const onPesquisar = async (pagination) => {
        const response = await onFind(pagination);
        const res = await onSetList(response);
        const total = response?.totalElements;
        setLista(res?.content ?? res);
        setTotalRecords(total);
    };

    const onFiltro = (filtros) => {
        pagination.filters = filtros;
        setPagination(pagination);
        onPesquisar(pagination);
    };

    useEffect(() => {
        const vRows = rows ? rows : 10;
        const vPage = page ? page : 0;

        setLazyParams({
            first: vPage > 0 ? vRows * vPage + 1 : 0,
            rows: vRows,
            page: vPage,
        });
        setStateRows(rows ? rows : 10);
    }, [page, rows]);

    const handleOnPage = (event) => {
        if (permitidoExecutarFuncao) {
            onPaginacao(event);

            let _lazyParams = { ...lazyParams, ...event };

            setLazyParams(_lazyParams);
            setStateRows(event.rows);
        } else {
            setPermitidoExecutarFuncao(true);
        }
    };

    useImperativeHandle(
        ref,
        () => {
            return {
                setLista: (data, setInitialData = false) => {
                    setLista(data);
                    if (setInitialData) {
                        setInitialTableData(data);
                    }
                },
                initialTableData,
                lista,
                expandedRows,
                setExpandedRows,
                onPesquisar,
                pagination,
                onFiltro,
            };
        },
        [lista, initialTableData]
    );

    if (isLoading) {
        return <TableLoader {...props} />;
    }

    return (
        <div
            className={`datatable-responsive-demo p-col-${col} p-md-${col} ${className} ${styles.table}`}
            style={{ overflowX: 'auto' }}
        >
            <DataTable
                value={lista}
                className={'p-datatable-responsive-demo'}
                dataKey={'id'}
                rowHover
                selection={selected}
                onSelectionChange={(e) => {
                    setSelected(e.value);
                    if (onSelectionChange) {
                        onSelectionChange(e.value);
                    }
                }}
                paginator={paginator}
                rows={stateRows}
                lazy={totalRecords ? true : false}
                emptyMessage={'Nenhum registro encontrado'}
                totalRecords={totalRecords}
                currentPageReportTemplate="Mostrando {first} à {last} de {totalRecords} registros"
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                rowsPerPageOptions={[5, 10, 25, 50]}
                onPage={handleOnPage}
                first={lazyParams.first}
                onRowSelect={onRowSelect}
                selectionPageOnly
                selectionMode={selectionMode ? selectionMode : null}
                responsiveLayout="stack"
                breakpoint="960px"
                rowExpansionTemplate={rowExpansionTemplate}
                onRowExpand={onRowExpand}
                onRowCollapse={onRowCollapse}
                expandedRows={expandedRows}
                onRowToggle={(e) => setExpandedRows(e.data)}
                {...rest}
            >
                {children}
            </DataTable>
        </div>
    );
};

export default forwardRef(SimpleTable);
